import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {SplitBookingBundle} from '@findhotel/sapi'
import {SplitBookingOfferInfo} from 'components/Offer/types'
import {useTrackPreferredRateOfferServed} from 'hooks/useTrackPreferredRateOfferServed'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getOfferAccessFlowSettings,
  getOfferHasGhaOrGvrPreferredRate
} from 'modules/sapiSearch/selectors'
import {getShouldShowYourChoiceTag} from 'modules/search/selectors'
import {getIsFromGha} from 'utils/trafficSource'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {OfferTagLabel, RateChecker} from '@vio/core/src/offer/types/offer'
import {OfferSecondaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {Cashback} from '@vio/shared/src/cashback/components/Cashback'
import {OfferLayout} from '@vio/shared/src/search/OfferLayout'

import {useShowTotalPriceGhaOrGvrPreferredRate} from '../hooks/useShowTotalPriceGhaOrGvrPreferredRate'
import {OfferInteraction} from '../Shared/OfferInteraction'
import {GHAPreferredRateTotalPrice} from '../Shared/OfferPrice/GHAPreferredRateTotalPrice'
import {SplitBookingOfferInteraction} from '../Shared/SplitBookingOfferInteraction'
import {YourChoiceLabel} from '../Shared/YourChoiceLabel'
import {CtaComponent} from './CtaComponent'
import {PriceComponent} from './PriceComponent'
import {ProviderLogoComponent} from './ProviderLogoComponent'
import {RoomLabelsComponent} from './RoomLabelsComponent'
import {YourChoiceAndFreeCancelationLabels} from './YourChoiceAndFreeCancelationLabels'

const OfferLayoutWrapper = styled.div`
  display: inline;
`

interface MainProps {
  isLoading?: false
  variant: OfferSecondaryLayoutVariant
  offer: SearchOffer
  anchorPriceTotal: number
  anchorPrice: number
  hasRoomLabels?: boolean
  showPriceIndication?: boolean
  hotelId: string
  sourceComponent?: string
  offerPosition?: number
  hasInteraction: boolean
  isSplitBooking?: false
  splitBookingOffers?: never
  splitBookingBundle?: never
  showCashback?: boolean
  cashbackVariant?: 'short' | 'long'
  hasArrowCTA?: boolean
  showProviderLogo?: boolean
}

interface SplitBookingProps {
  isLoading?: false
  variant: OfferSecondaryLayoutVariant
  isSplitBooking: boolean
  splitBookingOffers: SplitBookingOfferInfo[]
  splitBookingBundle: SplitBookingBundle
  hasInteraction?: false
  offer?: never
  hasRoomLabels?: never
  showPriceIndication?: never
  hotelId: string
  anchorPriceTotal?: never
  anchorPrice?: never
  sourceComponent?: string
  offerPosition?: never
  showCashback?: never
  cashbackVariant?: never
  hasArrowCTA?: boolean
  showProviderLogo?: boolean
}

interface LoadingProps {
  variant: OfferSecondaryLayoutVariant
  isLoading: true
  hasInteraction?: false
  offer?: never
  hasRoomLabels?: never
  showPriceIndication?: never
  hotelId?: never
  anchorPriceTotal?: never
  anchorPrice?: never
  isSplitBooking?: never
  splitBookingOffers?: never
  splitBookingBundle?: never
  sourceComponent?: never
  offerPosition?: never
  cashbackComponent?: never
  showCashback?: never
  cashbackVariant?: never
  hasArrowCTA?: never
  showProviderLogo?: boolean
}

export const SecondaryOffer: React.FC<
  MainProps | SplitBookingProps | LoadingProps
> = ({
  variant,
  offer,
  splitBookingOffers,
  splitBookingBundle,
  hasRoomLabels = true,
  showPriceIndication = false,
  hotelId,
  anchorPriceTotal,
  anchorPrice,
  isLoading = false,
  isSplitBooking,
  hasInteraction = true,
  offerPosition = 0,
  sourceComponent,
  showCashback,
  cashbackVariant,
  hasArrowCTA = true,
  showProviderLogo = true
}) => {
  const {isMobile} = useDeviceLayout()
  const currencyCode = useSelector(getCurrencyCode)

  const {isAppLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const shouldShowYourChoice = useSelector(
    getShouldShowYourChoiceTag(
      isSplitBooking && splitBookingOffers.length
        ? splitBookingOffers[0].offer
        : offer,
      isSplitBooking
    )
  )

  const shouldShowGhaOrGvrPreferredRateTotalPrice =
    useShowTotalPriceGhaOrGvrPreferredRate(offer, hotelId)

  const isFromGha = useSelector(getIsFromGha)

  const offerHasGhaOrGvrPreferredRate = useSelector(state =>
    getOfferHasGhaOrGvrPreferredRate(state, offer)
  )

  const isYourChoice = hasOfferTag(
    offer || splitBookingBundle,
    OfferTagLabel.IsYourChoice
  )
  useTrackPreferredRateOfferServed(isYourChoice)

  if (isLoading) {
    return <OfferLayout variant={variant} isLoading />
  }

  if (isSplitBooking) {
    return (
      <SplitBookingOfferInteraction
        component={sourceComponent}
        hotelId={hotelId}
      >
        <OfferLayoutWrapper
          data-id={isYourChoice && RateChecker.GHAYourChoiceId}
        >
          <OfferLayout
            variant={variant}
            priceComponent={<PriceComponent isSplitBooking hotelId={hotelId} />}
            providerLogoComponent={
              showProviderLogo ? (
                <ProviderLogoComponent
                  hotelId={hotelId}
                  isSplitBooking
                  variant={variant}
                  splitBookingOffers={splitBookingOffers}
                />
              ) : undefined
            }
            roomLabelsComponent={
              <RoomLabelsComponent
                isSplitBooking
                variant={variant}
                isYourChoice={shouldShowYourChoice}
              />
            }
            ctaComponent={<CtaComponent variant={variant} />}
            yourChoiceComponent={
              shouldShowYourChoice && variant === 'SecondaryWithoutRoomName' ? (
                <YourChoiceLabel />
              ) : null
            }
          />
        </OfferLayoutWrapper>
      </SplitBookingOfferInteraction>
    )
  }

  return (
    <OfferInteraction
      anchorPriceTotal={anchorPriceTotal}
      hotelId={hotelId}
      offer={offer}
      component={sourceComponent}
      offerPosition={offerPosition}
      hasInteraction={hasInteraction}
    >
      <OfferLayoutWrapper data-id={isYourChoice && RateChecker.GHAYourChoiceId}>
        <OfferLayout
          variant={variant}
          shouldShowFreeCancellationDate={offerHasGhaOrGvrPreferredRate}
          // NOTE: Do not show cashback for desktop secondary offers on the main hotel card
          cashbackComponent={
            showCashback && offer.cashback ? (
              <Cashback
                value={offer.cashback.value}
                type={offer.cashback.type}
                variant={cashbackVariant}
              />
            ) : null
          }
          priceComponent={
            <PriceComponent
              hotelId={hotelId}
              anchorPriceTotal={anchorPriceTotal}
              anchorPrice={anchorPrice}
              offer={offer}
              variant={variant}
              showGHAPreferredRateTotalPrice={
                isMobile && shouldShowGhaOrGvrPreferredRateTotalPrice
              }
              showPriceIndication={showPriceIndication}
            />
          }
          providerLogoComponent={
            showProviderLogo ? (
              <ProviderLogoComponent
                hotelId={hotelId}
                variant={variant}
                isAppLockedDeal={isAppLocked}
                offer={offer}
                sourceComponent={sourceComponent}
              />
            ) : undefined
          }
          roomLabelsComponent={
            <RoomLabelsComponent
              variant={variant}
              hasRoomLabels={hasRoomLabels}
              offer={offer}
              hotelId={hotelId}
              isAppLockedDeal={isAppLocked}
            />
          }
          ctaComponent={hasArrowCTA ? <CtaComponent variant={variant} /> : null}
          yourChoiceComponent={
            shouldShowYourChoice && (
              <>
                {!isMobile && shouldShowGhaOrGvrPreferredRateTotalPrice && (
                  <>
                    <GHAPreferredRateTotalPrice
                      value={offer.totalRate}
                      currency={currencyCode as string}
                      hasUnderline
                    />
                  </>
                )}
                <YourChoiceAndFreeCancelationLabels
                  offer={offer}
                  shouldShowFreeCancellation={
                    !isMobile &&
                    (shouldShowGhaOrGvrPreferredRateTotalPrice || isFromGha)
                  }
                />
              </>
            )
          }
        />
      </OfferLayoutWrapper>
    </OfferInteraction>
  )
}
